import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import noop from 'lib/function/noop'
import usePrevious from 'hooks/usePrevious'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import type { DrawerExperience } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import TripPlanDetailDrawer from '../components/TripPlanDetailDrawer'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import LocationInfoDrawer from '../components/LocationInfoDrawer'
import ChatOfferDetailDrawer from '../components/ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from '../components/ChatExperienceDetailDrawer'
import ChatCruiseOfferDetailDrawer from '../components/CruiseOfferDetailDrawer'
import { useDrawerStackState } from '../hooks/useDrawerStackState'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { usePlaceSearchFailure } from 'tripPlanner/assistant/contexts/PlaceSearchFailureContext'
import { usePlaceSearchResolver } from '../hooks/usePlaceSearchResolver'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { trackHotelDrawerOpened, trackExperienceDrawerOpened, trackCruiseDrawerOpened } from 'tripPlanner/utils/chatAnalytics'

interface DrawerContainerProps {
  closeSignal?: number
  onDrawerOpenChange?: (isOpen: boolean) => void
}

function DrawerContainer({
  closeSignal,
  onDrawerOpenChange = noop,
}: DrawerContainerProps = {}) {
  const eventBus = useTripPlannerEventBus()
  const [hasDomainDrawerBase, setHasDomainDrawerBase] = useState(false)
  const { sendMessage } = useChatActions()
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const { itineraryData, multiCartId, deleteManualItineraryTarget, isManualItineraryDeletePending } = useItineraryContext()
  const manualDeleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending

  const {
    topEntry,
    isAnyDrawerOpen,
    hasPreviousDrawer,
    shouldAnimateTopEntry,
    openDetailPanel,
    openPlaceInfo,
    openLocationInfo,
    openOfferDrawer,
    goBack,
    closeAllDrawers,
    afterClose,
    resetDrawers,
  } = useDrawerStackState()
  const checkoutSource = useMemo<App.CheckoutSource | undefined>(() => {
    if (!chatId || !multiCartId) return undefined
    return {
      type: 'trip_planner',
      multiCartId,
      externalReferenceId: String(chatId),
    }
  }, [chatId, multiCartId])

  const { resetFailures } = usePlaceSearchFailure()
  const { resolvePlace, cancel: cancelResolve } = usePlaceSearchResolver((googlePlaceId, placeName) => {
    openPlaceInfo({ googlePlaceId, placeName })
  })
  const previousCloseSignal = usePrevious(closeSignal, closeSignal)
  const previousIsAnyDrawerOpenRef = useRef(isAnyDrawerOpen)

  const closeDomainDrawers = useCallback(() => {
    setHasDomainDrawerBase(false)
    eventBus.dispatch({ type: 'close_right_panel_domain_drawers' })
  }, [eventBus])

  useEffect(() => {
    if (previousIsAnyDrawerOpenRef.current === isAnyDrawerOpen) return

    previousIsAnyDrawerOpenRef.current = isAnyDrawerOpen
    onDrawerOpenChange(isAnyDrawerOpen)
  }, [isAnyDrawerOpen, onDrawerOpenChange])

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignal) return

    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
  }, [cancelResolve, closeDomainDrawers, closeSignal, previousCloseSignal, resetDrawers])

  const trackedOfferDrawerIdsRef = useRef(new Set<string>())
  useEffect(() => {
    const isOfferDrawerOpen = isAnyDrawerOpen && topEntry?.kind === 'offerDrawer'
    const justOpened = isOfferDrawerOpen && !trackedOfferDrawerIdsRef.current.has(topEntry.id)
    if (!justOpened || !chatId) return

    trackedOfferDrawerIdsRef.current.add(topEntry.id)
    if (topEntry.data.offerType === 'experience') {
      trackExperienceDrawerOpened(chatId)
    } else if (topEntry.data.offerType === 'cruise') {
      trackCruiseDrawerOpened(chatId)
    } else {
      trackHotelDrawerOpened(chatId)
    }
  }, [isAnyDrawerOpen, topEntry, chatId])

  const handleDeleteStopFromDrawer = useCallback((stopId: number) => {
    if (manualDeleteActionsDisabled) return
    const stop = itineraryData?.stops.find((s) => s.id === stopId)
    if (!stop) return
    deleteManualItineraryTarget({ targetType: 'stop', stopId: stop.id })
  }, [manualDeleteActionsDisabled, itineraryData, deleteManualItineraryTarget])

  const closeEveryDrawer = useCallback(() => {
    closeAllDrawers()
    closeDomainDrawers()
  }, [closeAllDrawers, closeDomainDrawers])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    closeEveryDrawer()
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation,
        offerType: 'hotel',
      },
    })
  }, [closeEveryDrawer, eventBus])

  const handleExperienceDrawerShowMap = useCallback((experience: DrawerExperience) => {
    closeEveryDrawer()
    if (!experience.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: experience.offerId,
        name: experience.name,
        coordinates: experience.geoLocation,
        offerType: 'experience',
      },
    })
  }, [closeEveryDrawer, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('open_place_drawer', (event) => {
    openPlaceInfo({ googlePlaceId: event.googlePlaceId, placeName: event.placeName })
  })

  useEventSubscription('open_place_search_drawer', (event) => {
    resolvePlace({ searchQuery: event.searchQuery, placeName: event.placeName })
  })

  useEventSubscription('open_location_drawer', (event) => {
    openLocationInfo(event.data)
  })

  useEventSubscription('open_detail_drawer', (event) => {
    openDetailPanel({
      googlePlaceId: event.googlePlaceId,
      placeName: event.placeName,
      countryName: event.countryName,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_offer_drawer', (event) => {
    openOfferDrawer({
      offerId: event.offerId,
      accommodationPropertyId: event.accommodationPropertyId,
      searchParams: event.searchParams,
      offerType: event.offerType,
      scrollToPricing: event.scrollToPricing,
      scrollToRoomOptions: event.scrollToRoomOptions,
    })
  })

  const handleStackBack = useMemo(() => {
    if (!isAnyDrawerOpen) return undefined
    if (hasPreviousDrawer) return goBack
    if (hasDomainDrawerBase) return closeAllDrawers
    return undefined
  }, [closeAllDrawers, goBack, hasDomainDrawerBase, hasPreviousDrawer, isAnyDrawerOpen])

  const centralDrawerAnimation = shouldAnimateTopEntry && !hasDomainDrawerBase ? undefined : 'none'

  useEventSubscription('right_panel_domain_drawer_state_changed', (event) => {
    setHasDomainDrawerBase(event.isOpen)
  })

  useEventSubscription('reset_chat_state', () => {
    cancelResolve()
    resetDrawers()
    closeDomainDrawers()
    resetFailures()
  })

  return (
    <>
      {topEntry?.kind === 'detailPanel' && (
        <TripPlanDetailDrawer
          key={topEntry.id}
          isOpen={isAnyDrawerOpen}
          onDismiss={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
          googlePlaceId={topEntry.data.googlePlaceId}
          placeName={topEntry.data.placeName}
          countryName={topEntry.data.countryName}
          stopId={topEntry.data.stopId}
          onDeleteStop={topEntry.data.stopId !== undefined && !manualDeleteActionsDisabled ? handleDeleteStopFromDrawer : undefined}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          chatId={chatId}
        />
      )}
      {topEntry?.kind === 'placeInfo' && (
        <PlaceInfoDrawer
          key={topEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
          googlePlaceId={topEntry.data.googlePlaceId}
          placeName={topEntry.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {topEntry?.kind === 'locationInfo' && (
        <LocationInfoDrawer
          key={topEntry.id}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
          locationData={topEntry.data}
          category={topEntry.data.category}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {topEntry?.kind === 'offerDrawer' && topEntry.data.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          key={topEntry.id}
          offerId={topEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
          onShowMap={handleExperienceDrawerShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          scrollToPricing={topEntry.data.scrollToPricing}
          checkoutSource={checkoutSource}
        />
      )}
      {topEntry?.kind === 'offerDrawer' && topEntry.data.offerType === 'cruise' && (
        <ChatCruiseOfferDetailDrawer
          key={topEntry.id}
          offerId={topEntry.data.offerId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
        />
      )}
      {topEntry?.kind === 'offerDrawer' && topEntry.data.offerType !== 'experience' && topEntry.data.offerType !== 'cruise' && (
        <ChatOfferDetailDrawer
          key={topEntry.id}
          offerId={topEntry.data.offerId}
          accommodationPropertyId={topEntry.data.accommodationPropertyId}
          isOpen={isAnyDrawerOpen}
          onClose={closeEveryDrawer}
          onAfterClose={afterClose}
          onBack={handleStackBack}
          animation={centralDrawerAnimation}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          searchParams={topEntry.data.searchParams}
          scrollToRoomOptions={topEntry.data.scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </>
  )
}

export default DrawerContainer
